import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './contact-details.reducer';

export const ContactDetails = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const contactDetailsList = useAppSelector(state => state.contactDetails.entities);
  const loading = useAppSelector(state => state.contactDetails.loading);
  const totalItems = useAppSelector(state => state.contactDetails.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="contact-details-heading" data-cy="ContactDetailsHeading">
        <Translate contentKey="tinyMedicRestApp.contactDetails.home.title">Contact Details</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.contactDetails.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/contact-details/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.contactDetails.home.createLabel">Create new Contact Details</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.contactDetails.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {contactDetailsList && contactDetailsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('firstName')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.firstName">First Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('firstName')} />
                </th>
                <th className="hand" onClick={sort('middleName')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.middleName">Middle Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('middleName')} />
                </th>
                <th className="hand" onClick={sort('lastName')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.lastName">Last Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastName')} />
                </th>
                <th className="hand" onClick={sort('addressLine1')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.addressLine1">Address Line 1</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('addressLine1')} />
                </th>
                <th className="hand" onClick={sort('addressLine2')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.addressLine2">Address Line 2</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('addressLine2')} />
                </th>
                <th className="hand" onClick={sort('city')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.city">City</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('city')} />
                </th>
                <th className="hand" onClick={sort('countryCode')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.countryCode">Country Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('countryCode')} />
                </th>
                <th className="hand" onClick={sort('stateCode')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.stateCode">State Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stateCode')} />
                </th>
                <th className="hand" onClick={sort('pincode')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.pincode">Pincode</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('pincode')} />
                </th>
                <th className="hand" onClick={sort('contactNumber')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.contactNumber">Contact Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('contactNumber')} />
                </th>
                <th className="hand" onClick={sort('mobileNumber')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.mobileNumber">Mobile Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('mobileNumber')} />
                </th>
                <th className="hand" onClick={sort('faxNumber')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.faxNumber">Fax Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('faxNumber')} />
                </th>
                <th className="hand" onClick={sort('email')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.email">Email</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('email')} />
                </th>
                <th className="hand" onClick={sort('stayDuration')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.stayDuration">Stay Duration</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stayDuration')} />
                </th>
                <th className="hand" onClick={sort('createDtm')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.createDtm">Create Dtm</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createDtm')} />
                </th>
                <th className="hand" onClick={sort('createdBy')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.createdBy">Created By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdBy')} />
                </th>
                <th className="hand" onClick={sort('modifiedBy')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.modifiedBy">Modified By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('modifiedBy')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('relationCodeId')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.relationCodeId">Relation Code Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('relationCodeId')} />
                </th>
                <th className="hand" onClick={sort('latitude')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.latitude">Latitude</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('latitude')} />
                </th>
                <th className="hand" onClick={sort('longitude')}>
                  <Translate contentKey="tinyMedicRestApp.contactDetails.longitude">Longitude</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('longitude')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contactDetailsList.map((contactDetails, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/contact-details/${contactDetails.id}`} color="link" size="sm">
                      {contactDetails.id}
                    </Button>
                  </td>
                  <td>{contactDetails.firstName}</td>
                  <td>{contactDetails.middleName}</td>
                  <td>{contactDetails.lastName}</td>
                  <td>{contactDetails.addressLine1}</td>
                  <td>{contactDetails.addressLine2}</td>
                  <td>{contactDetails.city}</td>
                  <td>{contactDetails.countryCode}</td>
                  <td>{contactDetails.stateCode}</td>
                  <td>{contactDetails.pincode}</td>
                  <td>{contactDetails.contactNumber}</td>
                  <td>{contactDetails.mobileNumber}</td>
                  <td>{contactDetails.faxNumber}</td>
                  <td>{contactDetails.email}</td>
                  <td>{contactDetails.stayDuration}</td>
                  <td>{contactDetails.createDtm}</td>
                  <td>{contactDetails.createdBy}</td>
                  <td>{contactDetails.modifiedBy}</td>
                  <td>{contactDetails.timestampentry}</td>
                  <td>{contactDetails.microSec}</td>
                  <td>{contactDetails.lastupdated}</td>
                  <td>{contactDetails.active ? 'true' : 'false'}</td>
                  <td>{contactDetails.cursor}</td>
                  <td>{contactDetails.relationCodeId}</td>
                  <td>{contactDetails.latitude}</td>
                  <td>{contactDetails.longitude}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/contact-details/${contactDetails.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/contact-details/${contactDetails.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/contact-details/${contactDetails.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.contactDetails.home.notFound">No Contact Details found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={contactDetailsList && contactDetailsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ContactDetails;
